<script lang="ts">
  /**
   * Veksttidslinje — bildene ESP32-CAM laster opp til Supabase Storage
   * (bucket `plantebilder`, mappe = potte_id).
   *
   *  - Stort bilde med glidebryter: dra gjennom dagene, eller spill av som timelapse.
   *  - Bilder tatt mens lyset var av, er nesten svarte. De finnes automatisk og
   *    hoppes over (lib/bildeanalyse).
   *  - «Grønt i bildet» per bilde gir en vekstkurve. Analysen skjer i nettleseren
   *    på en nedskalert kopi, og resultatet huskes per bilde på enheten, så hvert
   *    bilde bare analyseres én gang.
   * Trykk på bildet → stor visning (lukkes også med tilbake-knappen og Escape).
   */
  import { onMount, onDestroy } from 'svelte';
  import { supabase } from '../lib/supabase';
  import { overlayOpened } from '../lib/overlayBack';
import { maalPiksler, erMorkt, vekstEndring, vekstKurvePunkter, type BildeMaal } from '../lib/bildeanalyse';

  type Bilde = { navn: string; url: string; dato: Date };

  let { potteId, fra = null, til = null }: { potteId: string; fra?: string | null; til?: string | null } = $props();

  let bilder = $state<Bilde[]>([]);
  let laster = $state(true);
  let feil = $state(false);
  let maal = $state<Record<string, BildeMaal>>({});
  let idx = $state(0);
  let folgSiste = $state(true); // står på nyeste bilde til brukeren drar selv
  let spiller = $state(false);
  let aapent = $state<Bilde | null>(null);
  let bildeFeil = $state<Record<string, boolean>>({});
  let avbrutt = false;

  const CACHE_KEY = 'plantepotte:bildeanalyse:v2';
  const MAKS_ANALYSE = 120; // de nyeste; 2 bilder/dag = to måneder
  const TIMELAPSE_MS = 280;

  // Maskinvare-/nettleser-tilbake lukker den forstørrede visningen.
  $effect(() => {
    if (aapent) return overlayOpened(() => (aapent = null));
  });

  // Mørke bilder (lyset av) hoppes over når de er analysert.
  const synlige = $derived(
    bilder.filter((b) => !bildeFeil[b.navn] && !(maal[b.navn] && erMorkt(maal[b.navn]!))),
  );
  const antallBildeFeil = $derived(Object.values(bildeFeil).filter(Boolean).length);
  const morke = $derived(
    bilder.filter((b) => !bildeFeil[b.navn] && maal[b.navn] && erMorkt(maal[b.navn]!)).length,
  );
  $effect(() => {
    if (folgSiste) idx = Math.max(0, synlige.length - 1);
  });
  const valgt = $derived(synlige[Math.min(idx, synlige.length - 1)]);
  const vekst = $derived(
    synlige.filter((b) => maal[b.navn]).map((b) => ({ dato: b.dato, gront: maal[b.navn]!.gront })),
  );
  const endring = $derived(vekstEndring(vekst));
  // Før plantene kommer opp er kurven bare støy rundt 0 — vis den først når
  // minst ett bilde har litt grønt.
  const visVekst = $derived(vekst.some((v) => v.gront >= 0.02));

  function markerBildeFeilet(bilde: Bilde) {
    bildeFeil = { ...bildeFeil, [bilde.navn]: true };
    if (aapent?.navn === bilde.navn) aapent = null;
  }

  function datoFraNavn(navn: string): Date {
    // Kameraet skriver 20260922-191321.jpg i UTC
    const m = navn.match(/(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
    if (m) return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!));
    return new Date(0);
  }
  function formatDato(d: Date): string {
    return d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  }
  function formatFull(d: Date): string {
    return d.toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function lesCache(): Record<string, Record<string, BildeMaal>> {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const cache = raw ? JSON.parse(raw) : {};
      return cache && typeof cache === 'object' && !Array.isArray(cache)
        ? (cache as Record<string, Record<string, BildeMaal>>)
        : {};
    } catch {
      return {};
    }
  }
  function skrivCache(m: Record<string, BildeMaal>) {
    try {
      const cache = lesCache();
      const perPotte = { ...(cache[potteId] ?? {}), ...m };
      const nokler = Object.keys(perPotte).sort().slice(-600); // navnene er tidsstempler
      cache[potteId] = Object.fromEntries(nokler.map((k) => [k, perPotte[k]]));
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
      /* privat modus e.l. — da analyseres bildene på nytt neste gang */
    }
  }

  function lastBilde(url: string): Promise<HTMLImageElement> {
    return new Promise((ok, avvis) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => ok(img);
      img.onerror = avvis;
      img.src = url;
    });
  }

  async function analyser(liste: Bilde[]) {
    const lerret = document.createElement('canvas');
    lerret.width = 64;
    lerret.height = 48;
    const ctx = lerret.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const nye: Record<string, BildeMaal> = {};
    for (const b of liste.slice(-MAKS_ANALYSE).reverse()) {
      if (avbrutt) break;
      if (maal[b.navn]) continue;
      try {
        const img = await lastBilde(b.url);
        if (avbrutt) break;
        ctx.drawImage(img, 0, 0, 64, 48);
        const m = maalPiksler(ctx.getImageData(0, 0, 64, 48).data);
        nye[b.navn] = { lys: Math.round(m.lys), gront: Math.round(m.gront * 1000) / 1000 };
        maal = { ...maal, [b.navn]: nye[b.navn]! };
      } catch {
        /* bildet kunne ikke leses — vises likevel, bare uten analyse */
      }
    }
    if (!avbrutt && Object.keys(nye).length > 0) skrivCache(nye);
  }

  onMount(async () => {
    maal = lesCache()[potteId] ?? {};
    // Hent alle sider før periodefilteret: en gammel runde kan ligge bak
    // hundrevis av nyere bilder. De nyeste 300 i valgt runde vises.
    const filer = [];
    let listeFeil = false;
    for (let offset = 0; !avbrutt; offset += 300) {
      const svar = await supabase.storage.from('plantebilder')
        .list(potteId, { limit: 300, offset, sortBy: { column: 'name', order: 'desc' } })
        .catch(() => null);
      if (!svar || svar.error) { listeFeil = true; break; }
      filer.push(...(svar.data ?? []));
      if ((svar.data?.length ?? 0) < 300) break;
    }
    // Kassebytte avmonterer denne komponenten. En treg Storage-respons må da
    // ikke fylle den forrige tidslinjen med data etter at brukeren har gått videre.
    if (avbrutt) return;
    if (listeFeil) {
      feil = true;
      laster = false;
      return;
    }
    bilder = filer
      .filter((f) => f.name && !f.name.startsWith('.') && /\.(jpe?g|png|webp)$/i.test(f.name))
      .map((f) => ({
        navn: f.name,
        dato: f.created_at ? new Date(f.created_at) : datoFraNavn(f.name),
      }))
      .filter((b) => (!fra || b.dato.getTime() >= Date.parse(fra)) && (!til || b.dato.getTime() < Date.parse(til)))
      .sort((a, b) => a.dato.getTime() - b.dato.getTime())
      .slice(-300)
      .map((b) => ({ ...b, url: supabase.storage.from('plantebilder').getPublicUrl(`${potteId}/${b.navn}`).data.publicUrl }));
    laster = false;
    analyser(bilder);
  });

  let timer: ReturnType<typeof setInterval> | undefined;
  function stopp() {
    spiller = false;
    if (timer) clearInterval(timer);
    timer = undefined;
  }
  function spillAv() {
    if (spiller) return stopp();
    if (synlige.length < 2) return;
    // Last alle bildene før avspillingen, så den ikke hakker
    for (const b of synlige) new Image().src = b.url;
    folgSiste = false;
    if (idx >= synlige.length - 1) idx = 0;
    spiller = true;
    timer = setInterval(() => {
      if (idx >= synlige.length - 1) {
        stopp();
        folgSiste = true;
      } else idx += 1;
    }, TIMELAPSE_MS);
  }
  onDestroy(() => {
    avbrutt = true;
    stopp();
  });

  // Vekstkurve: x = tid, y = grønt i bildet
  const kurve = $derived.by(() => {
    if (vekst.length < 2) return [];
    return vekstKurvePunkter(vekst).map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`);
  });
  const pct = (x: number) => Math.round(x * 100);
</script>

<section class="card p-5">
  <div class="flex items-baseline justify-between gap-3">
    <h2 class="font-display text-lg font-semibold">Veksttidslinje</h2>
    {#if synlige.length > 0}
      <span class="font-mono text-[11px] text-text-muted">{synlige.length} {synlige.length === 1 ? 'bilde' : 'bilder'}</span>
    {/if}
  </div>

  {#if synlige.length > 0 && valgt}
    <button class="block w-full mt-3" onclick={() => (aapent = valgt)} aria-label="Vis bildet stort">
      <img
        src={valgt.url}
        alt="Plantebilde {formatFull(valgt.dato)}"
        onerror={() => markerBildeFeilet(valgt)}
        class="w-full aspect-[4/3] object-cover rounded-xl border border-border bg-surface-raised"
      />
    </button>
    <div class="flex items-center gap-3 mt-3">
      <button
        class="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-leaf/15 text-leaf hover:bg-leaf/25 transition-colors disabled:opacity-40"
        onclick={spillAv}
        disabled={synlige.length < 2}
        aria-label={spiller ? 'Pause' : 'Spill av som timelapse'}
      >
        {#if spiller}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
        {:else}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4v16l13-8z"/></svg>
        {/if}
      </button>
      <input
        type="range"
        min="0"
        max={Math.max(0, synlige.length - 1)}
        bind:value={idx}
        oninput={() => {
          stopp();
          folgSiste = idx >= synlige.length - 1;
        }}
        disabled={synlige.length < 2}
        class="slider-lys flex-1"
        style="--pct: {synlige.length > 1 ? (idx / (synlige.length - 1)) * 100 : 100}%"
        aria-label="Velg bilde"
      />
    </div>
    <div class="font-mono text-[11px] text-text-muted mt-1.5 text-center">{formatFull(valgt.dato)}</div>

    {#if visVekst}
      <div class="mt-4 pt-3 border-t border-border">
        <div class="flex items-baseline justify-between gap-3">
          <span class="text-sm">Grønt i bildet</span>
          <span class="font-mono text-sm">{pct(vekst[vekst.length - 1]!.gront)} %</span>
        </div>
        {#if kurve.length > 0}
          <div role="group" aria-label="Vekstgraf med fast skala fra 0 til 100 prosent">
            <svg viewBox="0 0 300 40" class="w-full h-10 mt-1.5" preserveAspectRatio="none" aria-hidden="true">
              <line x1="0" y1="4" x2="300" y2="4" stroke="#596273" stroke-opacity="0.35" stroke-width="1" />
              <line x1="0" y1="36" x2="300" y2="36" stroke="#596273" stroke-opacity="0.35" stroke-width="1" />
              <polyline points={kurve.join(' ')} fill="none" stroke="#4ade80" stroke-width="2" vector-effect="non-scaling-stroke" stroke-linejoin="round" />
          </svg>
            <p class="sr-only">
              Andelen grønt endret seg {endring?.poeng ?? 0} prosentpoeng fra {pct(vekst[0]!.gront)} prosent {formatFull(vekst[0]!.dato)}
              til {pct(vekst[vekst.length - 1]!.gront)} prosent {formatFull(vekst[vekst.length - 1]!.dato)}.
            </p>
            <ol class="sr-only" aria-label="Målepunkter i vekstgrafen">
              {#each vekst as punkt (`${punkt.dato.getTime()}-${punkt.gront}`)}
                <li>{formatFull(punkt.dato)}: {pct(punkt.gront)} prosent grønt i bildet.</li>
              {/each}
            </ol>
          </div>
        {/if}
        <p class="text-[11px] text-text-muted leading-snug mt-1">
          {#if endring}
            {endring.poeng >= 0 ? '+' : ''}{endring.poeng} prosentpoeng siden {formatDato(endring.fra.dato)}
            ·
          {/if}
          anslag ut fra fargene i bildet.
        </p>
      </div>
    {/if}

    {#if morke > 0}
      <p class="text-[11px] text-text-dim mt-2">
        {morke} {morke === 1 ? 'mørkt bilde' : 'mørke bilder'} hoppet over (tatt mens lyset var av).
      </p>
    {/if}
    {#if antallBildeFeil > 0}
      <p class="text-[11px] text-text-dim mt-2" role="status">
        {antallBildeFeil} {antallBildeFeil === 1 ? 'bilde kunne' : 'bilder kunne'} ikke lastes og er hoppet over.
      </p>
    {/if}
  {:else if laster}
    <div class="text-text-dim text-sm animate-pulse py-4" role="status">Henter bilder…</div>
  {:else if feil}
    <div class="text-text-muted text-sm py-2" role="alert">
      Kunne ikke hente bilder akkurat nå. Sjekk nettet — de hentes på nytt neste gang du åpner kassa.
    </div>
  {:else if antallBildeFeil > 0}
    <div class="text-text-muted text-sm py-2" role="status">
      {antallBildeFeil} {antallBildeFeil === 1 ? 'bilde kunne' : 'bilder kunne'} ikke lastes. Prøv igjen ved å åpne kassa på nytt.
    </div>
  {:else if bilder.length > 0}
    <div class="text-text-dim text-sm py-2">Alle bildene er tatt mens lyset var av.</div>
  {:else}
    <div class="text-text-dim text-sm py-2">Ingen bilder ennå — kameraet tar ett bilde om dagen, kl. 12.</div>
  {/if}
</section>

<!-- Escape lukker forstørrelsen på PC (tilbake-knappen gjør det på mobil) -->
<svelte:window
  onkeydown={(e) => {
    if (aapent && e.key === 'Escape') aapent = null;
  }}
/>

{#if aapent}
  {@const a = aapent}
  <div
    class="fixed inset-0 z-50 flex flex-col items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
    role="presentation"
    onclick={() => (aapent = null)}
  >
    <button
      class="absolute right-3 p-2 rounded-full bg-black/45 text-white hover:bg-black/65 transition-colors"
      style="top: calc(0.75rem + env(safe-area-inset-top))"
      onclick={() => (aapent = null)}
      aria-label="Lukk bildet"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    </button>
    <img src={a.url} alt="Plantebilde {formatFull(a.dato)}" onerror={() => markerBildeFeilet(a)} class="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
    <div class="font-mono text-xs text-text-muted mt-3">{formatFull(a.dato)}</div>
  </div>
{/if}
